import React, { useContext, useEffect } from 'react';
import { RouteProps, useLocation, useHistory } from 'react-router-dom';
import { auth } from '../../../utils/auth';
import WithLoading from '../../../hocs/WithLoading/WithLoading';
import { useAuth } from '../../../contexts/AuthContext';
import { MembershipContext } from '../../../contexts/MembershipContext';
import getMembershipInfo from '../../../utils/get-membership-info';
import getEntities from '../../../utils/get-entities';
import { membershipCondition } from '../../../utils/membership-condition';
import { MEMBERSHIP_COUNTRY_UNITED_STATES } from '../../../utils/constants';

const SSOCallbackRoute: React.FC<RouteProps> = (): JSX.Element | null => {
    const location = useLocation();
    const history = useHistory();
    const query = new URLSearchParams(location.search);
    const { refresh, setLoadingState } = useAuth();
    const { setMembershipInfo, setCountries, setCareerTypes } = useContext(MembershipContext);

    const Placeholder = (): JSX.Element => <></>;
    const PlaceholderWithLoading = WithLoading(Placeholder);

    const fetchAndStoreData = async (): Promise<string> => {
        try {
            // Fetch both membership info and entities in parallel
            const [membershipInfoResponse, countriesResponse, careerTypesResponse] = await Promise.all([
                getMembershipInfo(),
                getEntities('countries'),
                getEntities('career-types'),
            ]);

            // Store membership info in context
            setMembershipInfo(membershipInfoResponse);

            // Sort countries to put United States first (same logic as useEntities)
            const index = countriesResponse.findIndex((item) => item.value === MEMBERSHIP_COUNTRY_UNITED_STATES);
            if (index > 0) {
                const sortedCountries = [countriesResponse[index], ...countriesResponse];
                sortedCountries.splice(index + 1, 1);
                setCountries(sortedCountries);
            } else {
                setCountries(countriesResponse);
            }

            setCareerTypes(careerTypesResponse);

            // Calculate the redirect path based on membership status
            const condition = membershipCondition(
                membershipInfoResponse.contact.membershipStatus,
                membershipInfoResponse.contact.cesAudit,
                membershipInfoResponse.contact.renewYear,
                membershipInfoResponse.renewEligibleYears,
                membershipInfoResponse.contact.membershipEpaymentStatus,
            );
            return `/${condition}`;
        } catch (error) {
            console.error('Error fetching membership data:', error);
            return '/'; // Fallback to home if data fetch fails
        }
    };

    useEffect(() => {
        const handleCallback = async () => {
            try {
                setLoadingState(true);
                // Check if we're returning from provisioning
                const accountProvisioned = query.get('account_provisioned');

                if (accountProvisioned === 'true') {
                    // Refresh tokens after successful provisioning
                    await auth.refreshTokens();
                    await refresh();
                    // Fetch all data and store in context, then determine correct path
                    const redirectPath = await fetchAndStoreData();
                    // Wait for next frame to ensure React has committed DOM updates
                    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve(undefined))));
                    
                    // Preserve any additional query parameters (except auth-related ones)
                    const params = new URLSearchParams(location.search);
                    params.delete('account_provisioned');
                    const queryString = params.toString();
                    
                    history.replace({
                        pathname: redirectPath,
                        search: queryString,
                    });
                    return;
                }

                // Normal OAuth callback flow
                const code = query.get('code');
                const state = query.get('state');

                if (!code || !state) {
                    throw new Error('Missing code or state parameter');
                }

                await auth.exchangeToken(code, state);
                await refresh();
                // Fetch all data and store in context, then determine correct path
                const redirectPath = await fetchAndStoreData();
                // Wait for next frame to ensure React has committed DOM updates
                await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve(undefined))));
                
                // Preserve any additional query parameters (except auth-related ones)
                const params = new URLSearchParams(location.search);
                params.delete('code');
                params.delete('state');
                const queryString = params.toString();
                
                history.replace({
                    pathname: redirectPath,
                    search: queryString,
                });
            } catch (error) {
                console.error('Authentication error:', error);
                await refresh(); // Ensure state is cleared even on error
                await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve(undefined))));
                history.replace('/');
            }
        };

        handleCallback();
    }, []);

    // Show app-standard loading spinner during token exchange
    return <PlaceholderWithLoading loading={true} error={false} />;
};

export default SSOCallbackRoute;
